import { clientAccessService } from '../../services/clientAccessService';
import { ClientUserAccessList } from '../../types/clientAccess';

/*
 * The users of a client, remembered for the session so a client opened
 * twice shows at once, and prefetched when the pointer rests on a client
 * row so the click lands on a list that is already there. The screen still
 * re-reads in the background: what it shows first is a few seconds old at
 * most, and never older than TTL.
 */

const TTL_MS = 5 * 60 * 1000;

interface Entry {
  at: number;
  list: ClientUserAccessList;
}

const entries = new Map<string, Entry>();
const inFlight = new Map<string, Promise<ClientUserAccessList>>();

/** What is remembered for a client, fresh or not; null when nothing is. */
export const peekClientUsers = (clientId: string): ClientUserAccessList | null => {
  const hit = entries.get(clientId);
  if (!hit) return null;
  if (Date.now() - hit.at > TTL_MS) {
    entries.delete(clientId);
    return null;
  }
  return hit.list;
};

/** Read the client's users, one request at a time per client, and remember the answer. */
export const loadClientUsers = (clientId: string): Promise<ClientUserAccessList> => {
  const pending = inFlight.get(clientId);
  if (pending) return pending;
  const promise = clientAccessService
    .fetchClientUsers(clientId, true)
    .then((list) => {
      entries.set(clientId, { at: Date.now(), list });
      return list;
    })
    .finally(() => inFlight.delete(clientId));
  inFlight.set(clientId, promise);
  return promise;
};

/** Start reading in the background; a failure is simply not remembered. */
export const prefetchClientUsers = (clientId: string): void => {
  if (peekClientUsers(clientId) || inFlight.has(clientId)) return;
  loadClientUsers(clientId).catch(() => undefined);
};

export const forgetClientUsers = (clientId?: string): void => {
  if (clientId) entries.delete(clientId);
  else entries.clear();
};
